import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Calendar, BarChart3, TrendingUp, TrendingDown, Wallet, RefreshCw, Download, User, Printer, Pencil, Trash2, Warehouse } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const R = (n) => 'R ' + Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MONTH_NAMES = { '01':'Jan','02':'Feb','03':'Mar','04':'Apr','05':'May','06':'Jun','07':'Jul','08':'Aug','09':'Sep','10':'Oct','11':'Nov','12':'Dec' };
const fmtMonth = (ym) => {
  if (!ym) return '-';
  const [y, m] = ym.split('-');
  return `${MONTH_NAMES[m] || m} ${y}`;
};
const fmtDay = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-ZA', { weekday: 'short', day: '2-digit', month: 'short' });
};

export default function CashUpPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Day-drill-down state (used for both day AND month)
  const [drillDown, setDrillDown] = useState(null); // { type: 'day'|'month', key: 'YYYY-MM-DD' | 'YYYY-MM' }
  const [dayData, setDayData] = useState(null);
  const [dayLoading, setDayLoading] = useState(false);

  // Edit-sale dialog state (lets admins amend OLD sales from any past day/month)
  const [editingSale, setEditingSale] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/auto-spares/admin/cash-up`, { headers });
      setData(res.data);
    } catch {
      toast.error('Failed to load cash-up data');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDay = async (date) => {
    setDrillDown({ type: 'day', key: date });
    setDayData(null);
    setDayLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/auto-spares/admin/sales-by-date`, {
        headers,
        params: { date },
      });
      setDayData(res.data);
    } catch {
      toast.error('Failed to load day sales');
    } finally {
      setDayLoading(false);
    }
  };

  const openMonth = async (month) => {
    setDrillDown({ type: 'month', key: month });
    setDayData(null);
    setDayLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/auto-spares/admin/sales-by-month`, {
        headers,
        params: { month },
      });
      setDayData(res.data);
    } catch {
      toast.error('Failed to load month sales');
    } finally {
      setDayLoading(false);
    }
  };

  const refreshDrill = async () => {
    if (!drillDown) return;
    if (drillDown.type === 'day') await openDay(drillDown.key);
    else await openMonth(drillDown.key);
  };

  const handleDeleteSale = async (saleId) => {
    if (!window.confirm('Delete this sale? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/auto-spares/admin/sales/${saleId}`, { headers });
      toast.success('Sale deleted');
      await Promise.all([refreshDrill(), fetchData()]);
    } catch {
      toast.error('Failed to delete sale');
    }
  };

  const handleSaveEditSale = async () => {
    if (!editingSale) return;
    try {
      await axios.put(`${API_URL}/api/auto-spares/admin/sales/${editingSale.id}`, {
        part_description: editingSale.part_description || '',
        quantity: editingSale.quantity,
        unit_price: editingSale.unit_price,
        date: editingSale.date,
        payment_method: editingSale.payment_method || 'cash',
      }, { headers });
      toast.success('Sale updated');
      setEditingSale(null);
      await Promise.all([refreshDrill(), fetchData()]);
    } catch {
      toast.error('Failed to update sale');
    }
  };

  const exportCsv = (rows, filename, headersArr) => {
    if (!rows || rows.length === 0) { toast.error('Nothing to export'); return; }
    const header = headersArr.map(h => h.label).join(',');
    const body = rows.map(r => headersArr.map(h => r[h.key]).join(',')).join('\n');
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const printReceipt = () => {
    if (!dayData || !drillDown) return;
    const d = dayData;
    const isMonth = drillDown.type === 'month';
    // Find running balance for this day/month from the overview data (if available)
    let runningBalance = null;
    if (data) {
      if (isMonth) {
        const match = data.monthly?.find(m => m.month === drillDown.key);
        runningBalance = match?.running_balance;
      } else {
        const match = data.daily?.find(x => x.date === drillDown.key);
        runningBalance = match?.running_balance;
      }
    }
    const win = window.open('', '_blank', 'width=420,height=800');
    if (!win) { toast.error('Pop-up blocked — allow pop-ups to print'); return; }
    const salesRows = d.sales.map(s => `
      <tr>
        ${isMonth ? `<td>${s.date || ''}</td>` : ''}
        <td>${s.salesman_name || '-'}</td>
        <td>${(s.part_description || s.category || '-').toString().slice(0, 22)}</td>
        <td class="c">${s.quantity}</td>
        <td class="r">${R(s.total)}</td>
      </tr>`).join('');
    const expRows = d.expenses.map(e => `
      <tr>
        ${isMonth ? `<td>${e.date || ''}</td>` : ''}
        <td>${(e.description || '-').toString().slice(0, 24)}</td>
        <td>${e.category || '-'}</td>
        <td class="r">-${R(e.amount)}</td>
      </tr>`).join('');
    const when = isMonth ? fmtMonth(drillDown.key) : fmtDay(drillDown.key);
    const now = new Date().toLocaleString('en-ZA');
    const salesHead = isMonth
      ? '<tr><th>Date</th><th>Staff</th><th>Part</th><th class="c">Qty</th><th class="r">Total</th></tr>'
      : '<tr><th>Staff</th><th>Part</th><th class="c">Qty</th><th class="r">Total</th></tr>';
    const expHead = isMonth
      ? '<tr><th>Date</th><th>Description</th><th>Category</th><th class="r">Amount</th></tr>'
      : '<tr><th>Description</th><th>Category</th><th class="r">Amount</th></tr>';
    const expColspan = isMonth ? 4 : 3;
    win.document.write(`<!doctype html>
<html><head><meta charset="utf-8"><title>Cash-Up Receipt ${drillDown.key}</title>
<style>
  *{box-sizing:border-box;font-family:'Courier New',monospace}
  body{margin:0;padding:14px;color:#000;background:#fff;font-size:11px;width:${isMonth ? '420' : '320'}px}
  h1{font-size:16px;text-align:center;margin:0 0 4px;letter-spacing:2px}
  .sub{text-align:center;font-size:11px;color:#444;margin-bottom:8px}
  .line{border-top:1px dashed #000;margin:6px 0}
  table{width:100%;border-collapse:collapse;font-size:10px}
  th,td{padding:2px 4px;vertical-align:top}
  th{text-align:left;border-bottom:1px solid #000}
  td.r,th.r{text-align:right}
  td.c,th.c{text-align:center}
  .tot{display:flex;justify-content:space-between;font-weight:bold;font-size:12px;margin:3px 0}
  .big{font-size:14px}
  .neg{color:#b91c1c}
  .sect{text-align:center;font-weight:bold;font-size:11px;margin:8px 0 4px;letter-spacing:1px}
  .footer{text-align:center;font-size:10px;color:#555;margin-top:12px;border-top:1px dashed #000;padding-top:6px}
  @media print{body{width:auto}.noprint{display:none}}
</style></head><body>
  <h1>JSM AUTO SPARES</h1>
  <div class="sub">Cash-Up Receipt${isMonth ? ' — Monthly' : ''}</div>
  <div class="line"></div>
  <div class="tot"><span>${isMonth ? 'Month:' : 'Date:'}</span><span>${when}</span></div>
  <div class="tot"><span>Printed:</span><span>${now}</span></div>
  <div class="line"></div>

  <div class="sect">SALES (${d.totals.sales_count})</div>
  ${d.sales.length === 0 ? '<div style="text-align:center;font-style:italic;padding:4px">No sales recorded.</div>' : `
  <table>
    <thead>${salesHead}</thead>
    <tbody>${salesRows}</tbody>
  </table>`}

  <div class="line"></div>
  <div class="tot"><span>Sales Total</span><span>${R(d.totals.sales_total)}</span></div>
  <div class="tot"><span>Cost of Goods</span><span>${R(d.totals.cost_total)}</span></div>
  <div class="tot"><span>Gross Profit</span><span>${R(d.totals.gross_profit)}</span></div>

  <div class="sect">EXPENSES (${d.expenses.length})</div>
  ${d.expenses.length === 0 ? '<div style="text-align:center;font-style:italic;padding:4px">No expenses recorded.</div>' : `
  <table>
    <thead>${expHead}</thead>
    <tbody>${expRows}
      <tr><td colspan="${expColspan - 1}"><b>TOTAL EXPENSES</b></td><td class="r"><b>-${R(d.totals.expenses_total)}</b></td></tr>
    </tbody>
  </table>`}

  <div class="line"></div>
  <div class="tot big ${d.totals.cash_on_hand < 0 ? 'neg' : ''}">
    <span>CASH ON HAND</span><span>${R(d.totals.cash_on_hand)}</span>
  </div>
  <div class="sub" style="font-size:10px;margin-top:2px">= Sales (${R(d.totals.sales_total)}) − Expenses (${R(d.totals.expenses_total)})</div>
  ${runningBalance != null ? `
  <div class="line"></div>
  <div class="tot big ${runningBalance < 0 ? 'neg' : ''}">
    <span>RUNNING BALANCE</span><span>${R(runningBalance)}</span>
  </div>
  <div class="sub" style="font-size:10px;margin-top:2px">Cumulative cash on hand up to this ${isMonth ? 'month' : 'date'}.</div>` : ''}
  <div class="line"></div>

  <div class="footer">
    JSM Trading &middot; Cash-up generated by JSM Portal<br/>
    Prepared by: ________________ &nbsp; Sign: ________________
  </div>
  <script>window.onload = () => { window.print(); };</script>
</body></html>`);
    win.document.close();
  };

  if (loading) return <div className="text-center py-10 text-slate-600">Loading cash-up...</div>;
  if (!data) return null;

  const { today, month_to_date: mtd, daily, monthly } = data;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Wallet className="h-5 w-5 text-emerald-700" /> Sales & Cash-Up
          </h2>
          <p className="text-slate-600 text-sm">Daily &amp; monthly sales with cash-up balance (Sales − Expenses).</p>
        </div>
        <Button variant="outline" onClick={fetchData} className="bg-slate-900 border-slate-200 text-white hover:bg-slate-700" data-testid="cashup-refresh">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Today's Sales" value={today.sales} hint={`${today.transactions} sale${today.transactions === 1 ? '' : 's'}`} color="emerald" icon={TrendingUp} />
        <SummaryCard label="Today's Expenses" value={today.expenses} color="orange" icon={TrendingDown} />
        <SummaryCard label="Today's Cash Up" value={today.cash_on_hand} color={today.cash_on_hand >= 0 ? 'blue' : 'rose'} icon={Wallet} bold />
        <SummaryCard label="Month-to-Date Sales" value={mtd.sales} hint={`Cash up: ${R(mtd.cash_on_hand)}`} color="indigo" icon={BarChart3} />
      </div>

      {/* Payment Method Breakdown — Today */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-pm-breakdown">
        {[
          { k: 'cash',   label: 'Cash',   bg: 'from-emerald-500 to-emerald-600' },
          { k: 'online', label: 'Online', bg: 'from-blue-500 to-blue-600' },
          { k: 'eft',    label: 'EFT',    bg: 'from-violet-500 to-violet-600' },
          { k: 'card',   label: 'Card',   bg: 'from-amber-500 to-amber-600' },
        ].map(opt => {
          const tval = today?.payment_breakdown?.[opt.k] || 0;
          const mval = mtd?.payment_breakdown?.[opt.k] || 0;
          return (
            <div key={opt.k} className={`rounded-lg bg-gradient-to-br ${opt.bg} text-white p-3`} data-testid={`admin-pm-tile-${opt.k}`}>
              <div className="text-xs opacity-80 uppercase tracking-wide">{opt.label} Today</div>
              <p className="text-xl font-bold">{R(tval)}</p>
              <div className="text-[10px] opacity-70 mt-0.5">MTD: {R(mval)}</div>
            </div>
          );
        })}
        {(() => {
          const tb = today?.payment_breakdown || {};
          const mb = mtd?.payment_breakdown || {};
          const tSum = (Number(tb.cash) || 0) + (Number(tb.eft) || 0) + (Number(tb.online) || 0) + (Number(tb.card) || 0);
          const mSum = (Number(mb.cash) || 0) + (Number(mb.eft) || 0) + (Number(mb.online) || 0) + (Number(mb.card) || 0);
          return (
            <div className="rounded-lg bg-gradient-to-br from-slate-700 to-slate-800 text-white p-3 border-2 border-slate-400 shadow-lg" data-testid="admin-pm-tile-total">
              <div className="text-xs opacity-80 uppercase tracking-wide">Total On Hand</div>
              <p className="text-2xl font-extrabold">{R(tSum)}</p>
              <div className="text-[10px] opacity-70 mt-0.5">MTD: {R(mSum)}</div>
            </div>
          );
        })()}
      </div>

      {/* Daily breakdown */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base text-white flex items-center gap-2">
            <Calendar className="h-4 w-4 text-blue-700" /> Sales per Day — {fmtMonth(mtd.month)}
          </CardTitle>
          <Button
            size="sm"
            variant="ghost"
            className="text-slate-700 hover:text-white h-8"
            onClick={() => exportCsv(daily, `sales-per-day-${mtd.month}.csv`, [
              { key: 'date', label: 'Date' },
              { key: 'count', label: 'Transactions' },
              { key: 'sales', label: 'Sales' },
              { key: 'expenses', label: 'Expenses' },
              { key: 'cash_on_hand', label: 'Daily Cash Up' },
              { key: 'running_balance', label: 'Running Balance' },
            ])}
          >
            <Download className="h-3.5 w-3.5 mr-1" /> CSV
          </Button>
        </CardHeader>
        <CardContent>
          {daily.length === 0 ? (
            <p className="text-slate-600 text-center py-6">No sales yet this month.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-200">
                  <TableHead className="text-slate-600">Date</TableHead>
                  <TableHead className="text-slate-600 text-center"># Sales</TableHead>
                  <TableHead className="text-slate-600 text-right">Sales</TableHead>
                  <TableHead className="text-slate-600 text-right">Expenses</TableHead>
                  <TableHead className="text-slate-600 text-right">Daily Cash Up</TableHead>
                  <TableHead className="text-slate-600 text-right">Running Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {daily.map(d => (
                  <TableRow
                    key={d.date}
                    className="border-slate-200 hover:bg-slate-700/40 cursor-pointer"
                    data-testid={`daily-row-${d.date}`}
                    onClick={() => openDay(d.date)}
                    title="Click to see all sales on this day"
                  >
                    <TableCell className="text-white font-medium underline-offset-2 hover:underline">{fmtDay(d.date)}</TableCell>
                    <TableCell className="text-slate-700 text-center">{d.count}</TableCell>
                    <TableCell className="text-emerald-700 text-right font-semibold">{R(d.sales)}</TableCell>
                    <TableCell className="text-orange-700 text-right">{R(d.expenses)}</TableCell>
                    <TableCell className={`text-right font-bold ${d.cash_on_hand >= 0 ? 'text-blue-700' : 'text-rose-700'}`}>{R(d.cash_on_hand)}</TableCell>
                    <TableCell className={`text-right font-extrabold ${d.running_balance >= 0 ? 'text-cyan-300' : 'text-rose-300'}`}>{R(d.running_balance)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-slate-200 bg-slate-700/40 font-bold">
                  <TableCell className="text-white">MONTH TOTAL</TableCell>
                  <TableCell className="text-slate-200 text-center">{daily.reduce((a, b) => a + b.count, 0)}</TableCell>
                  <TableCell className="text-emerald-700 text-right">{R(daily.reduce((a, b) => a + b.sales, 0))}</TableCell>
                  <TableCell className="text-orange-700 text-right">{R(daily.reduce((a, b) => a + b.expenses, 0))}</TableCell>
                  <TableCell className="text-blue-700 text-right text-base">{R(daily.reduce((a, b) => a + b.cash_on_hand, 0))}</TableCell>
                  <TableCell className="text-cyan-300 text-right text-base">{R(daily.length ? daily[0].running_balance : 0)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Monthly breakdown */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base text-white flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-violet-700" /> Sales per Month — Last 12 Months
          </CardTitle>
          <Button
            size="sm"
            variant="ghost"
            className="text-slate-700 hover:text-white h-8"
            onClick={() => exportCsv(monthly, 'sales-per-month.csv', [
              { key: 'month', label: 'Month' },
              { key: 'count', label: 'Transactions' },
              { key: 'sales', label: 'Sales' },
              { key: 'expenses', label: 'Expenses' },
              { key: 'cash_on_hand', label: 'Monthly Cash Up' },
              { key: 'running_balance', label: 'Running Balance' },
            ])}
          >
            <Download className="h-3.5 w-3.5 mr-1" /> CSV
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-slate-200">
                <TableHead className="text-slate-600">Month</TableHead>
                <TableHead className="text-slate-600 text-center"># Sales</TableHead>
                <TableHead className="text-slate-600 text-right">Sales</TableHead>
                <TableHead className="text-slate-600 text-right">Expenses</TableHead>
                <TableHead className="text-slate-600 text-right">Monthly Cash Up</TableHead>
                <TableHead className="text-slate-600 text-right">Running Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {monthly.map(m => (
                <TableRow
                  key={m.month}
                  className="border-slate-200 hover:bg-slate-700/40 cursor-pointer"
                  data-testid={`monthly-row-${m.month}`}
                  onClick={() => openMonth(m.month)}
                  title="Click to see all sales & expenses for this month"
                >
                  <TableCell className="text-white font-medium underline-offset-2 hover:underline">{fmtMonth(m.month)}</TableCell>
                  <TableCell className="text-slate-700 text-center">{m.count}</TableCell>
                  <TableCell className="text-emerald-700 text-right font-semibold">{R(m.sales)}</TableCell>
                  <TableCell className="text-orange-700 text-right">{R(m.expenses)}</TableCell>
                  <TableCell className={`text-right font-bold ${m.cash_on_hand >= 0 ? 'text-blue-700' : 'text-rose-700'}`}>{R(m.cash_on_hand)}</TableCell>
                  <TableCell className={`text-right font-extrabold ${m.running_balance >= 0 ? 'text-cyan-300' : 'text-rose-300'}`}>{R(m.running_balance)}</TableCell>
                </TableRow>
              ))}
              <TableRow className="border-slate-200 bg-slate-700/40 font-bold">
                <TableCell className="text-white">12-MONTH TOTAL</TableCell>
                <TableCell className="text-slate-200 text-center">{monthly.reduce((a, b) => a + b.count, 0)}</TableCell>
                <TableCell className="text-emerald-700 text-right">{R(monthly.reduce((a, b) => a + b.sales, 0))}</TableCell>
                <TableCell className="text-orange-700 text-right">{R(monthly.reduce((a, b) => a + b.expenses, 0))}</TableCell>
                <TableCell className="text-blue-700 text-right text-base">{R(monthly.reduce((a, b) => a + b.cash_on_hand, 0))}</TableCell>
                <TableCell className="text-cyan-300 text-right text-base">{R(monthly.length ? monthly[0].running_balance : 0)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Day drill-down dialog */}
      <Dialog open={!!drillDown} onOpenChange={(o) => { if (!o) { setDrillDown(null); setDayData(null); } }}>
        <DialogContent className="bg-slate-900 border-slate-200 text-white max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="day-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <Calendar className="h-5 w-5 text-blue-700" />
              <span>
                {drillDown?.type === 'month'
                  ? `All Sales & Expenses — ${fmtMonth(drillDown.key)}`
                  : `Sales for ${drillDown && fmtDay(drillDown.key)}`}
              </span>
              {dayData && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={printReceipt}
                  className="ml-auto bg-slate-800 border-slate-300 hover:bg-slate-700 text-white h-8"
                  data-testid="print-receipt-btn"
                >
                  <Printer className="h-3.5 w-3.5 mr-1.5" /> Print Receipt
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>

          {dayLoading && <div className="text-slate-600 text-center py-8">Loading...</div>}

          {!dayLoading && dayData && (
            <div className="space-y-4">
              {/* Summary strip */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
                <div className="bg-emerald-100 border border-emerald-500/40 rounded-lg p-3">
                  <div className="text-[10px] uppercase text-emerald-300 tracking-wider">Sales</div>
                  <div className="font-bold text-emerald-300">{R(dayData.totals.sales_total)}</div>
                  <div className="text-[10px] text-slate-600">{dayData.totals.sales_count} transactions</div>
                </div>
                <div className="bg-slate-700/50 border border-slate-300 rounded-lg p-3">
                  <div className="text-[10px] uppercase text-slate-600 tracking-wider">Cost of Goods</div>
                  <div className="font-bold text-slate-200">{R(dayData.totals.cost_total)}</div>
                </div>
                <div className="bg-violet-100 border border-purple-500/40 rounded-lg p-3">
                  <div className="text-[10px] uppercase text-purple-300 tracking-wider">Gross Profit</div>
                  <div className="font-bold text-purple-300">{R(dayData.totals.gross_profit)}</div>
                </div>
                <div className="bg-orange-100 border border-orange-500/40 rounded-lg p-3">
                  <div className="text-[10px] uppercase text-orange-300 tracking-wider">Expenses</div>
                  <div className="font-bold text-orange-300">{R(dayData.totals.expenses_total)}</div>
                </div>
                <div className={`border rounded-lg p-3 ${dayData.totals.cash_on_hand >= 0 ? 'bg-blue-100 border-blue-500/40' : 'bg-rose-100 border-rose-500/40'}`}>
                  <div className={`text-[10px] uppercase tracking-wider ${dayData.totals.cash_on_hand >= 0 ? 'text-blue-300' : 'text-rose-300'}`}>Cash Up</div>
                  <div className={`font-extrabold ${dayData.totals.cash_on_hand >= 0 ? 'text-blue-300' : 'text-rose-300'}`}>{R(dayData.totals.cash_on_hand)}</div>
                </div>
              </div>

              {/* Sales by payment-method breakdown (Cash + EFT + Online + Card = on-hand sales total) */}
              {(dayData.totals.payment_breakdown && dayData.sales.length > 0) && (() => {
                const pb = dayData.totals.payment_breakdown;
                const cash = Number(pb.cash || 0);
                const eft = Number(pb.eft || 0);
                const online = Number(pb.online || 0);
                const card = Number(pb.card || 0);
                const sum = cash + eft + online + card;
                const tile = (lbl, val, color, testid) => (
                  <div className={`rounded-lg border p-2 ${color}`} data-testid={testid}>
                    <div className="text-[10px] uppercase tracking-wider opacity-80">{lbl}</div>
                    <div className="font-bold text-base">{R(val)}</div>
                  </div>
                );
                return (
                  <div className="mb-3">
                    <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5 text-emerald-300">
                      <TrendingUp className="h-4 w-4" /> Sales by Payment Method
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="pm-breakdown">
                      {tile('Cash',   cash,   'bg-emerald-100 border-emerald-500/40 text-emerald-200', 'pm-cash')}
                      {tile('EFT',    eft,    'bg-violet-100 border-purple-500/40 text-purple-200',   'pm-eft')}
                      {tile('Online', online, 'bg-blue-100 border-blue-500/40 text-blue-200',         'pm-online')}
                      {tile('Card',   card,   'bg-amber-100 border-amber-500/40 text-amber-200',     'pm-card')}
                      <div className="rounded-lg border-2 p-2 bg-slate-700 border-slate-400 text-white shadow-lg" data-testid="pm-total">
                        <div className="text-[10px] uppercase tracking-wider opacity-90">Total Sales (On Hand)</div>
                        <div className="font-extrabold text-base">{R(sum)}</div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Sales list */}
              <div>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5 text-emerald-300">
                  <TrendingUp className="h-4 w-4" /> Sales ({dayData.sales.length})
                </h3>
                {dayData.sales.length === 0 ? (
                  <p className="text-slate-600 text-sm text-center py-3 border border-slate-200 rounded-lg bg-slate-800/30">No sales recorded.</p>
                ) : (
                  <div className="rounded-lg border border-slate-200 overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-200 bg-slate-800/60">
                          {drillDown?.type === 'month' && <TableHead className="text-slate-600">Date</TableHead>}
                          <TableHead className="text-slate-600">Salesman</TableHead>
                          <TableHead className="text-slate-600">Part</TableHead>
                          <TableHead className="text-slate-600 text-center">Qty</TableHead>
                          <TableHead className="text-slate-600 text-right">Unit Price</TableHead>
                          <TableHead className="text-slate-600 text-right">Total</TableHead>
                          <TableHead className="text-slate-600 text-center">Method</TableHead>
                          <TableHead className="text-slate-600 text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dayData.sales.map(s => {
                          const pm = (s.payment_method || 'cash').toLowerCase();
                          const pmStyles = { cash: 'bg-emerald-600', online: 'bg-blue-600', eft: 'bg-purple-600', card: 'bg-amber-600' };
                          return (
                          <TableRow key={s.id} className="border-slate-200">
                            {drillDown?.type === 'month' && <TableCell className="text-slate-700 font-mono text-xs">{s.date || '-'}</TableCell>}
                            <TableCell className="text-white flex items-center gap-1.5">
                              <User className="h-3 w-3 text-slate-500" />
                              {s.salesman_name || '-'}
                            </TableCell>
                            <TableCell className="text-slate-200">{s.part_description || s.category || '-'}</TableCell>
                            <TableCell className="text-center text-slate-700">{s.quantity}</TableCell>
                            <TableCell className="text-right text-slate-700">{R(s.unit_price)}</TableCell>
                            <TableCell className="text-right text-emerald-700 font-semibold">{R(s.total)}</TableCell>
                            <TableCell className="text-center">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-semibold text-white ${pmStyles[pm] || 'bg-slate-700'}`}>{pm.toUpperCase()}</span>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  size="sm"
                                  className="bg-blue-600 hover:bg-blue-700 text-white h-7 px-2"
                                  data-testid={`edit-old-sale-${s.id}`}
                                  onClick={() => setEditingSale({ ...s })}
                                  title="Edit this sale"
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-rose-600 hover:bg-rose-700 text-white h-7 px-2"
                                  data-testid={`delete-old-sale-${s.id}`}
                                  onClick={() => handleDeleteSale(s.id)}
                                  title="Delete this sale"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              {/* Expenses list — operating expenses + stock purchases sub-section */}
              <div>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5 text-orange-300">
                  <TrendingDown className="h-4 w-4" /> Operating Expenses ({dayData.expenses.length})
                </h3>
                {dayData.expenses.length === 0 ? (
                  <p className="text-slate-600 text-sm text-center py-3 border border-slate-200 rounded-lg bg-slate-800/30">No operating expenses on this {drillDown?.type === 'month' ? 'month' : 'day'}.</p>
                ) : (
                  <div className="rounded-lg border border-slate-200 overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-200 bg-slate-800/60">
                          {drillDown?.type === 'month' && <TableHead className="text-slate-600">Date</TableHead>}
                          <TableHead className="text-slate-600">Description</TableHead>
                          <TableHead className="text-slate-600">Category</TableHead>
                          <TableHead className="text-slate-600 text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dayData.expenses.map(e => (
                          <TableRow key={e.id} className="border-slate-200">
                            {drillDown?.type === 'month' && <TableCell className="text-slate-700 font-mono text-xs">{e.date || '-'}</TableCell>}
                            <TableCell className="text-white">{e.description || '-'}</TableCell>
                            <TableCell className="text-slate-700">{e.category || '-'}</TableCell>
                            <TableCell className="text-right text-orange-700 font-semibold">{R(e.amount)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="border-slate-200 bg-slate-700/40 font-bold">
                          <TableCell colSpan={drillDown?.type === 'month' ? 3 : 2} className="text-white">Operating Expenses Subtotal</TableCell>
                          <TableCell className="text-right text-orange-300">{R(dayData.totals.operating_expenses_total || 0)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Stock Purchases sub-section — split by payment_source.
                    Only CASH-paid stock comes out of the drawer. Card/EFT/Bank
                    are treated as CAPITAL INJECTIONS (owner-funded) and
                    DO NOT decrease cash on hand. */}
                <h3 className="text-sm font-semibold mt-4 mb-2 flex items-center gap-1.5 text-purple-300" data-testid="stock-purchases-section">
                  <Warehouse className="h-4 w-4" /> Stock Purchases ({(dayData.stock_purchases || []).length})
                </h3>
                {(!dayData.stock_purchases || dayData.stock_purchases.length === 0) ? (
                  <p className="text-slate-600 text-sm text-center py-3 border border-slate-200 rounded-lg bg-slate-800/30">No stock purchases on this {drillDown?.type === 'month' ? 'month' : 'day'}.</p>
                ) : (
                  <div className="rounded-lg border border-slate-200 overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-200 bg-slate-800/60">
                          {drillDown?.type === 'month' && <TableHead className="text-slate-600">Date</TableHead>}
                          <TableHead className="text-slate-600">Supplier / Note</TableHead>
                          <TableHead className="text-slate-600">PO #</TableHead>
                          <TableHead className="text-slate-600 text-center w-20">Paid Via</TableHead>
                          <TableHead className="text-slate-600 text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dayData.stock_purchases.map(p => {
                          const src = (p.payment_source || 'cash').toLowerCase();
                          const isCash = src === 'cash';
                          return (
                            <TableRow key={p.id} className="border-slate-200">
                              {drillDown?.type === 'month' && <TableCell className="text-slate-700 font-mono text-xs">{p.date || '-'}</TableCell>}
                              <TableCell className="text-white">
                                {p.supplier_name || p.note || '-'}
                                {p.note && p.supplier_name && <div className="text-xs text-slate-500">{p.note}</div>}
                              </TableCell>
                              <TableCell className="text-slate-600 font-mono text-xs">{p.po_number || '—'}</TableCell>
                              <TableCell className="text-center">
                                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${isCash ? 'bg-rose-700/70 text-white' : 'bg-emerald-700/70 text-white'}`} title={isCash ? 'Cash — comes out of the drawer' : 'Capital Injection — owner-funded, does NOT reduce cash on hand'}>
                                  {src}
                                </span>
                              </TableCell>
                              <TableCell className={`text-right font-semibold ${isCash ? 'text-rose-700' : 'text-emerald-700'}`}>{R(p.amount)}</TableCell>
                            </TableRow>
                          );
                        })}
                        {/* Two subtotal rows: cash (deducted) vs capital (not deducted) */}
                        {(dayData.totals.stock_purchases_cash || 0) > 0 && (
                          <TableRow className="border-slate-200 bg-rose-950/30 font-semibold">
                            <TableCell colSpan={drillDown?.type === 'month' ? 4 : 3} className="text-rose-200">
                              ↓ CASH stock purchases (deducts cash on hand)
                            </TableCell>
                            <TableCell className="text-right text-rose-300">- {R(dayData.totals.stock_purchases_cash)}</TableCell>
                          </TableRow>
                        )}
                        {(dayData.totals.capital_injections_total || 0) > 0 && (
                          <TableRow className="border-slate-200 bg-emerald-950/30 font-semibold">
                            <TableCell colSpan={drillDown?.type === 'month' ? 4 : 3} className="text-emerald-200">
                              ⊕ CAPITAL INJECTIONS (card / eft / bank — does NOT touch cash)
                            </TableCell>
                            <TableCell className="text-right text-emerald-300">{R(dayData.totals.capital_injections_total)}</TableCell>
                          </TableRow>
                        )}
                        <TableRow className="border-slate-200 bg-slate-700/40 font-bold">
                          <TableCell colSpan={drillDown?.type === 'month' ? 4 : 3} className="text-white">Stock Purchases Total</TableCell>
                          <TableCell className="text-right text-purple-300">{R(dayData.totals.stock_purchases_total || 0)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Combined Total */}
                <div className="mt-3 rounded-lg border border-orange-700/50 bg-orange-950/30 px-4 py-2 flex items-center justify-between">
                  <div className="text-orange-200 font-semibold">TOTAL OUTFLOW (Operating + Cash Stock only)</div>
                  <div className="text-orange-300 font-bold text-lg">{R(dayData.totals.expenses_total)}</div>
                </div>
                {(dayData.totals.capital_injections_total || 0) > 0 && (
                  <div className="mt-2 text-xs text-emerald-300 italic">
                    Capital injections of <b>{R(dayData.totals.capital_injections_total)}</b> are excluded from Cash on Hand and routed to capital. They funded stock without touching the till.
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit OLD sale dialog (admin amends past sales without leaving cash-up) */}
      <Dialog open={!!editingSale} onOpenChange={(o) => { if (!o) setEditingSale(null); }}>
        <DialogContent className="bg-slate-900 border-slate-200 text-white max-w-md" data-testid="edit-old-sale-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-blue-700" /> Edit Sale
            </DialogTitle>
          </DialogHeader>
          {editingSale && (
            <div className="space-y-3">
              <div>
                <Label className="text-slate-700 text-xs">Date</Label>
                <Input
                  type="date"
                  value={(editingSale.date || '').slice(0, 10)}
                  onChange={(e) => setEditingSale({ ...editingSale, date: e.target.value })}
                  className="bg-white border-slate-200 text-white"
                  data-testid="edit-sale-date"
                />
              </div>
              <div>
                <Label className="text-slate-700 text-xs">Part Description</Label>
                <Input
                  value={editingSale.part_description || ''}
                  onChange={(e) => setEditingSale({ ...editingSale, part_description: e.target.value })}
                  className="bg-white border-slate-200 text-white"
                  data-testid="edit-sale-part"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-700 text-xs">Quantity</Label>
                  <Input
                    type="number"
                    min={1}
                    value={editingSale.quantity ?? 1}
                    onChange={(e) => setEditingSale({ ...editingSale, quantity: parseInt(e.target.value, 10) || 0 })}
                    className="bg-white border-slate-200 text-white"
                    data-testid="edit-sale-qty"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 text-xs">Unit Price (R)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    value={editingSale.unit_price ?? 0}
                    onChange={(e) => setEditingSale({ ...editingSale, unit_price: parseFloat(e.target.value) || 0 })}
                    className="bg-white border-slate-200 text-white"
                    data-testid="edit-sale-price"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-700 text-xs">Payment Method</Label>
                <div className="grid grid-cols-4 gap-2 mt-1">
                  {[
                    { v: 'cash',   label: 'Cash',   cls: 'bg-emerald-600 hover:bg-emerald-700 border-emerald-500' },
                    { v: 'online', label: 'Online', cls: 'bg-blue-600 hover:bg-blue-700 border-blue-500' },
                    { v: 'eft',    label: 'EFT',    cls: 'bg-purple-600 hover:bg-purple-700 border-purple-500' },
                    { v: 'card',   label: 'Card',   cls: 'bg-amber-600 hover:bg-amber-700 border-amber-500' },
                  ].map(opt => {
                    const cur = (editingSale.payment_method || 'cash').toLowerCase();
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => setEditingSale({ ...editingSale, payment_method: opt.v })}
                        className={`rounded-md border px-2 py-1.5 text-xs font-semibold text-white transition ${cur === opt.v ? opt.cls + ' ring-2 ring-white/40' : 'bg-white border-slate-200 hover:bg-slate-700'}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="text-sm text-slate-600 bg-slate-800/60 border border-slate-200 rounded-md p-2">
                New total: <span className="text-emerald-700 font-semibold">{R((editingSale.quantity || 0) * (editingSale.unit_price || 0))}</span>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleSaveEditSale}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="save-edit-sale-btn"
                >
                  Save Changes
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setEditingSale(null)}
                  className="bg-white border-slate-200 text-white hover:bg-slate-700"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value, hint, color, icon: Icon, bold = false }) {
  const palette = {
    emerald: 'from-emerald-700/70 to-emerald-600/70 border-emerald-500/40 text-emerald-300',
    orange:  'from-orange-700/70 to-orange-600/70 border-orange-500/40 text-orange-300',
    blue:    'from-blue-700/70 to-blue-600/70 border-blue-500/40 text-blue-300',
    indigo:  'from-indigo-700/70 to-indigo-600/70 border-indigo-500/40 text-indigo-300',
    rose:    'from-rose-700/70 to-rose-600/70 border-rose-500/40 text-rose-300',
  }[color] || '';
  return (
    <div className={`rounded-xl border bg-gradient-to-br ${palette} p-4`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider opacity-80">{label}</span>
        <Icon className="h-4 w-4 opacity-80" />
      </div>
      <div className={`text-white mt-2 ${bold ? 'text-3xl font-extrabold' : 'text-2xl font-bold'}`}>{R(value)}</div>
      {hint && <div className="text-[11px] mt-1 opacity-80">{hint}</div>}
    </div>
  );
}
